import React, { useEffect, useReducer, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import Accordion from '@material-ui/core/Accordion'
import AccordionSummary from '@material-ui/core/AccordionSummary'
import AccordionDetails from '@material-ui/core/AccordionDetails'
import CloseIcon from '@material-ui/icons/Close'
import EqualizerIcon from '@material-ui/icons/Equalizer'
import Fab from '@material-ui/core/Fab'
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord'
import InfoIcon from '@material-ui/icons/Info'
import IconButton from '@material-ui/core/IconButton'
import MenuItem from '@material-ui/core/MenuItem'
import Paper from '@material-ui/core/Paper'
import Popover from '@material-ui/core/Popover'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import Select from '@material-ui/core/Select'
import Switch from '@material-ui/core/Switch'
import Typography from '@material-ui/core/Typography'
import mapboxgl from 'mapbox-gl/dist/mapbox-gl-csp'
import { circle } from '@turf/turf'
// eslint-disable-next-line import/no-webpack-loader-syntax
import Backdrop from '@material-ui/core/Backdrop'
import CircularProgress from '@material-ui/core/CircularProgress'

import { get } from '../../config/fetch'
import { getKpiColorByNetworkIndicator, infoIndicators } from '../heatmap/kpiUtils'
import Statistics from '../common/statistics'
import { PopupKpi, PopupBaseStation } from '../common/popup'
import { TOKEN_GET_BASE_STATION, TOKEN_GET_REPORT_BY_ID } from '../../constants/constants'

const eNBFromCid = cid => {
    if (cid) {
        const eNBHex = cid.toString(16).substring(0, 5)
        const eNB = parseInt(eNBHex, 16)
        return eNB
    }
}

const networkIndicators = {
    LTE: [
        { label: 'RSRP (dBm)', value: 'RSRP' },
        { label: 'RSRQ (dB)', value: 'RSRQ' },
        { label: 'RSSI (dBm)', value: 'RSSI' },
    ],
    WCDMA: [
        { label: 'RSCP', value: 'RSCP' },
        { label: 'Ec/Io (dB)', value: 'Ec/Io' },
    ],
}

const useStyles = makeStyles(theme => ({
    mapWrapper: {
        width: '100%',
        height: 'calc(100% - 64px)',
        position: 'relative',
    },
    accordionDetails: {
        display: 'block',
        width: '100%',
    },
    accordionKpiLegend: {
        backgroundColor: 'rgba(66,66,66,.5)',
    },
    wrapperLoader: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
}))

const StyledTableCell = withStyles(theme => ({
    head: {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
    },
    body: {
        fontSize: 14,
    },
}))(TableCell)

const StyledTableRow = withStyles(theme => ({
    root: {
        '&:nth-of-type(odd)': {
            backgroundColor: theme.palette.action.hover,
        },
    },
}))(TableRow)

function ReportDetail(props) {
    let loadingKpi = null

    const location = useLocation()
    const classes = useStyles()
    const mapContainer = useRef()
   
    const [anchorEl, setAnchorEl] = useState()
    const [visibleStatisticsModal, setVisibleStatisticsModal] = useState(false)

    const [map, setMap] = useState()
    const [report, setReport] = useState()
    const [baseStations, setBaseStations] = useState()
    const [kpis, setKpis] = useState()

    const [networkType, setNetworkType] = useState("")
    const [networkIndicator, setNetworkIndicator] = useState()
    const [showConnections, setShowConnections] = useState(false)

    const showLoadingKpi = (lng, lat, map) => {
        let loadingElement = document.createElement('div')
        loadingElement.className = 'marker-loading'
        loadingElement.style.backgroundImage = "url('../ball-triangle.svg')"
        loadingElement.style.width = '35px'
        loadingElement.style.height = '35px'
        loadingElement.style.backgroundSize = '100%'
        loadingKpi = new mapboxgl.Marker({ element: loadingElement })
            .setLngLat([lng, lat])
            .addTo(map)
    }

    const hideLoadingKpi = () => {
        loadingKpi.remove()
    }

    useEffect(() => {
        const id = location.pathname.split('/')[2]

        fetchReport(id)
            .then(async report => {
                setReport({
                    ...report,
                    ...getInfoFromUrl(report.url),
                })
            })
            .catch(e => {
                // TODO
            })
    }, [])

    useEffect(() => {
        if (report) {
            if(report.networkType){
                setNetworkType(report.networkType)
                setNetworkIndicator(report.networkIndicator || "")
            }
            
            const map = new mapboxgl.Map({
                container: mapContainer.current,
                style: 'mapbox://styles/mapbox/dark-v9',
                center: [report.longitude, report.latitude],
                zoom: 11,
            })
    
            map.on('load', async () => {
                setMap(map)  
            })
        }
    }, [report])

    useEffect(() => {
        if (map) {
            createKpiPopup()
            renderCircle(report.longitude, report.latitude, report.range, map)
            
            const bounds = getMapBounds(map)
            loadBaseStations(bounds)
            
            loadKpis(report, map)
        }
    }, [map])

    const createKpiPopup = () => {

        // Create a popup
        let popup = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
        })

        // Show popup when the mouse is over the kpi layer.
        map.on('mouseenter', 'kpi-circle', function (e) {
            map.getCanvas().style.cursor = 'pointer'

            var coordinates = e.features[0].geometry.coordinates.slice()
            var description = e.features[0].properties.description

            // Ensure that if the map is zoomed out such that multiple
            // copies of the feature are visible, the popup appears
            // over the copy being pointed to.
            while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360
            }

            popup.setLngLat(coordinates).setHTML(description).addTo(map)
        })

        map.on('mouseleave', 'kpi-circle', function () {
            map.getCanvas().style.cursor = ''
            popup.remove()
        })

    }

    useEffect(() => {
        if (baseStations) {
            renderBaseStations(baseStations, map)
        }
    }, [baseStations])

    useEffect(() => {
        if (kpis && baseStations && report) {
            renderKpis(kpis, baseStations, report.networkType, report.networkIndicator, map)
            renderKpisConnections(kpis, baseStations, report.networkType, map)
        }
    }, [kpis])

    useEffect(() => {
        if (map) {
            showKpisConnections(showConnections)
        }
    }, [showConnections])

    useEffect(() => {
        if (kpis && networkType && map) {
            renderKpis(kpis, baseStations, networkType, networkIndicator, map)
            renderKpisConnections(kpis, baseStations, networkType, map)
            setNetworkIndicator(null)
        }
    }, [networkType])

    const fetchReport = async id => {
        try {
            const { data: report } = await get(
                `/reports/${id}?code=${TOKEN_GET_REPORT_BY_ID}`,
            )

            return report
        } catch (e) {
            throw new Error(e)
        }
    }

    const fetchKpis = async url => {
        try {
            const { data: kpis } = await get(url)

            return kpis
        } catch (e) {
            throw new Error(e)
        }
    }

    const fetchBaseStations = async (bounds, networkType) => {
        try {
            const { data: baseStations } = await get(
                `/baseStations?bounds=${bounds}&networkType=${
                    networkType || ''
                }&code=${TOKEN_GET_BASE_STATION}`,
            )

            return baseStations
        } catch (e) {
            throw new Error(e)
        }
    }

    const loadBaseStations = async bounds => {
        const baseStations = await fetchBaseStations(bounds, networkType)
        setBaseStations(baseStations)
    }

    const loadKpis = async (report, map) => {
        showLoadingKpi(report.longitude, report.latitude, map)
        const kpis = await fetchKpis(report.url)
        setKpis(kpis)
        hideLoadingKpi()
    }

    const getInfoFromUrl = url => {
        const newUrl = new URL(`http://${url}`)

        const startDate = new Date(newUrl.searchParams.get('startDate'))
        const endDate = new Date(newUrl.searchParams.get('endDate'))
        const networkType = newUrl.searchParams.get('networkType')
        const range = Number(newUrl.searchParams.get('radius')) / 1000
        const latitude = Number(newUrl.searchParams.get('lat'))
        const longitude = Number(newUrl.searchParams.get('lng'))

        return { startDate, endDate, networkType, range, latitude, longitude }
    }

    const getMapBounds = map => {
        const ne = map.getBounds().getNorthEast()
        const coordsNe = JSON.stringify([ne.lng, ne.lat])
        const nw = map.getBounds().getNorthWest()
        const coordsNw = JSON.stringify([nw.lng, nw.lat])
        const se = map.getBounds().getSouthEast()
        const coordsSe = JSON.stringify([se.lng, se.lat])
        const sw = map.getBounds().getSouthWest()
        const coordsSw = JSON.stringify([sw.lng, sw.lat])
        const bounds = `[${coordsNw},${coordsNe},${coordsSe},${coordsSw},${coordsNw}]`

        return bounds
    }

    const handleClickPopover = event => {
        setAnchorEl(event.currentTarget)
    }

    const handleChangeNetworkType = e => {
        setNetworkType(e.target.value)
    }

    const renderCircle = (longitude, latitude, range, map) => {

        if (map.getSource('circleClicked')) {
            map.removeLayer('circleFill')
            map.removeLayer('circleOutline')
            map.removeSource('circleClicked')
        }
        
        const options = {
            steps: 64,
            units: 'kilometers',
            properties: { foo: 'bar' },
        }
        const newCircle = circle([longitude, latitude], range, options)

        // Add a data source containing GeoJSON data.
        map.addSource('circleClicked', {
            type: 'geojson',
            data: newCircle,
        })

        // Add a new layer to visualize the polygon.
        map.addLayer({
            id: 'circleFill',
            type: 'fill',
            source: 'circleClicked', // reference the data source
            layout: {},
            paint: {
                'fill-color': 'transparent', // blue color fill
                'fill-opacity': 0.5,
            },
        })

        // Add a black outline around the polygon.
        map.addLayer({
            id: 'circleOutline',
            type: 'line',
            source: 'circleClicked',
            layout: {},
            paint: {
                'line-color': 'yellow',
                'line-width': 3,
            },
        })
    }


    const deleteKpis = () => {
        // delete old kpis to render new kpis
        if (map.getSource('line')) {
            map.removeLayer('line-kpi')
            map.removeSource('line')
        }

        if (map.getSource('kpi')) {
            map.removeLayer('kpi-circle')
            map.removeSource('kpi')
        }
    }

    const renderKpis = async (kpis, baseStations, networkType, networkIndicator, map) => {

        deleteKpis()

    
        // render new kpi markers
        let kpiMarkers =  kpis.filter( kpi => kpi.connectivity.type == networkType ).map( kpi => {
             return {
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: kpi.location.coordinates,
                },
                properties: {
                    connectivity: kpi.connectivity,
                    color: getKpiColorByNetworkIndicator(networkIndicator, kpi.connectivity.signal),
                    description: PopupKpi(kpi),
                },
            }
        })

        //create Map source of kpis
        map.addSource('kpi', {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: kpiMarkers,
            },
        })

        map.addLayer({
            id: 'kpi-circle',
            type: 'circle',
            source: `kpi`,
            paint: {
                'circle-stroke-color': ['get', 'color'],
                'circle-color': ['get', 'color'],
                'circle-stroke-width': 1,
            },
        })

    }

    const renderKpisConnections = async (kpis, baseStations, networkType, map) => {

        let kpisConnections =  kpis.filter( kpi => kpi.connectivity.type == networkType ).reduce( (result, kpi) => {
            
            const bs =  baseStations.find(bs => {
                if (bs.cid && Number(bs.cid) === Number(kpi.connectivity.cell.cid)) {
                    return true
                } else if (bs.eNB) {
                    const kpiENB = eNBFromCid(kpi.connectivity.cell.cid)
                    return bs.eNB === kpiENB
                }
                return false
            })
            
            
            if (bs){
                result.push ({
                    type: 'Feature',
                    geometry: {
                        type: 'LineString',
                        coordinates: [
                            kpi.location.coordinates,
                            bs.location.coordinates,
                        ],
                    },
                })
            }

            return result
       }, [])


       if (map.getSource('kpi_connection_source')) {
            map.removeLayer('kpi_connections_layer')
            map.removeSource('kpi_connection_source')
        }
        
        // create Map source of lines (connections)
        map.addSource('kpi_connection_source', {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: kpisConnections,
            },
        })

        // create layer to source of lines connection
        map.addLayer({
            id: 'kpi_connections_layer',
            type: 'line',
            source: `kpi_connection_source`,
            layout: {
                'line-cap': 'round',
                'line-join': 'round',
                visibility: showConnections ? 'visible' : 'none',
            },
            paint: {
                'line-color': 'red',
                'line-width': 2,
                'line-opacity': 0.9,
            },
        })

    }

    const renderBaseStations = async (bs, map) => {
        bs.forEach(baseStation => {
            // create a DOM element for the marker
            renderMarkerBaseStation(baseStation, map)
        })
    }

    const renderMarkerBaseStation = (baseStation, map) => {
        let el = document.createElement('div')
        el.className = 'marker-basestation'
        el.style.backgroundImage =
            'url(https://iconsplace.com/wp-content/uploads/_icons/ffffff/256/png/radio-tower-icon-18-256.png)'
        el.style.width = '25px'
        el.style.height = '25px'
        el.style.backgroundSize = '100%'
        el.style.opacity = '0.4'

        el.onclick = function (e) {
            e.preventDefault()
        }

        // create the popup
        var popup = new mapboxgl.Popup({ offset: 25, className: 'my-class' })
            .setHTML(PopupBaseStation(baseStation))
            .setMaxWidth('300px')

        const marker = new mapboxgl.Marker({ element: el })
            .setLngLat(baseStation.location.coordinates)
            .setPopup(popup) // sets a popup on this marker
            .addTo(map)

        return marker
    }

    const renderNetworkIndicator = networkType => {
        if (networkType && networkType !== '') {
            return networkIndicators[networkType].map((item, index) => (
                <MenuItem key={`menuItem-${index}`} value={item.value}>
                    {item.label}
                </MenuItem>
            ))
        } else {
            return null
        }
    }

    const showKpisConnections = (show) => {
        if (map && map.getSource('kpi_connection_source')) {
            map.setLayoutProperty('kpi_connections_layer', 'visibility', show ? 'visible' : 'none')
        }
    }

    const handleChangeNetworkIndicator = e => {
        setNetworkIndicator(e.target.value)
        updateKpis(e.target.value)
    }

    const handleClosePopover = () => {
        setAnchorEl(null)
    }

    /**
     * Update kpi style (circle fill color)
     * depends of network indicator value
     * @param {String} newNetworkIndicator network indicator value
     */
    const updateKpis = newNetworkIndicator => {
        const kpisSource = map.getSource('kpi')
        if (kpisSource) {

            // New color of each kpi circle
            const kpisFeatures = kpisSource._data.features.map(kpi => {
                return {
                    geometry: kpi.geometry,
                    type: kpi.type,
                    properties: {
                        ...kpi.properties,
                        color: getKpiColorByNetworkIndicator(
                            newNetworkIndicator,
                            kpi.properties.connectivity.signal,
                        ),
                    },
                }
            })

            kpisSource.setData({
                type: 'FeatureCollection',
                features: kpisFeatures,
            })
        }
    }

    const renderTableHead = networkType => {
        if (networkType && networkType !== '') {
            return networkIndicators[networkType].map((item, index) => (
                <StyledTableCell key={`tableHead-${index}`} align="center">
                    {item.label}
                </StyledTableCell>
            ))
        } else {
            return null
        }
    }

    const renderTableRow = obj => {
        let row = []

        Object.keys(obj).map((item, index) => {
            if (index === 0) {
                row.push(
                    <StyledTableCell key={`tableRow-${index}`} component="th" scope="row">
                        <FiberManualRecordIcon htmlColor={obj[item]} />
                    </StyledTableCell>,
                )
            } else {
                row.push(
                    <StyledTableCell key={`tableRow-${index}`} align="center">
                        {obj[item]}
                    </StyledTableCell>,
                )
            }
        })

        return row
    }

    const startTime = report && report.startDate.toLocaleTimeString()
    const endTime = report && report.endDate.toLocaleTimeString()
    const openPopover = Boolean(anchorEl)
    const id = openPopover ? 'simple-popover' : undefined

    return (
        <div className={classes.mapWrapper}>
            <div style={{ width: 300, position: 'absolute', left: 10, top: 10, zIndex: 2 }}>
                {report && (
                    <div>
                        <Paper elevation={3}>
                            <Accordion expanded={true}>
                                <AccordionSummary
                                    expandIcon={null}
                                    aria-controls="panel1a-content"
                                    id="panel1a-header"
                                >
                                    <h2 style={{ margin: 0 }}>Report summary</h2>
                                </AccordionSummary>
                                <hr />
                                <AccordionDetails className={classes.accordionDetails}>
                                    <Typography shrink>Name</Typography>
                                    <p style={{ margin: 0 }}>{report.name}</p>
                                </AccordionDetails>
                                <AccordionDetails className={classes.accordionDetails}>
                                    <Typography shrink>Description</Typography>
                                    <p style={{ margin: 0 }}>{report.description}</p>
                                </AccordionDetails>
                                <AccordionDetails className={classes.accordionDetails}>
                                    <Typography shrink>Date</Typography>
                                    <p style={{ margin: 0 }}>{report.startDate.toDateString()}</p>
                                    <p style={{ margin: 0 }}>
                                        {startTime} - {endTime}
                                    </p>
                                </AccordionDetails>
                                <AccordionDetails className={classes.accordionDetails}>
                                    <Typography shrink>Radio (km)</Typography>
                                    <p style={{ margin: 0 }}>{report.range}</p>
                                </AccordionDetails>
                                {report.networkType && (
                                    <AccordionDetails className={classes.accordionDetails}>
                                        <Typography shrink>Network type</Typography>
                                        <p style={{ margin: 0 }}>{report.networkType}</p>
                                    </AccordionDetails>
                                )}
                            </Accordion>
                        </Paper>
                        {kpis && (
                            <Paper elevation={3} style={{ marginTop: '1em' }}>
                                <Accordion expanded={true}>
                                    <AccordionSummary
                                        expandIcon={null}
                                        aria-controls="panel1a-content"
                                        id="panel1a-header"
                                    >
                                        <h2 style={{ margin: 0 }}>Report controls</h2>
                                    </AccordionSummary>
                                    <hr />
                                    { !report.networkType && 
                                    <AccordionDetails className={classes.accordionDetails}>
                                        <Typography shrink>Network type</Typography>
                                        <Select
                                            labelId="network-type-label"
                                            value={networkType}
                                            onChange={handleChangeNetworkType}
                                            displayEmpty
                                            className={classes.selectEmpty}
                                        >
                                            <MenuItem value="">
                                                <em>Select one</em>
                                            </MenuItem>
                                            <MenuItem value="LTE">LTE</MenuItem>
                                            <MenuItem value="WCDMA">WCDMA</MenuItem>
                                        </Select>
                                    </AccordionDetails>}
                                    
                                    <AccordionDetails className={classes.accordionDetails}>
                                        <Typography shrink>Network indicator</Typography>
                                        <Select
                                            style={{ width: '100%' }}
                                            labelId="network-indicator-label"
                                            value={networkIndicator}
                                            onChange={handleChangeNetworkIndicator}
                                            displayEmpty
                                            className={classes.selectEmpty}
                                        >
                                            <MenuItem value={null}>
                                                <em>Select network indicator</em>
                                            </MenuItem>

                                            {renderNetworkIndicator(networkType)}
                                        </Select>
                                    </AccordionDetails>
                                    
                                    <AccordionDetails className={classes.accordionDetails}>
                                        <Typography shrink>Show connections</Typography>
                                        <Switch
                                            checked={showConnections}
                                            onChange={() => setShowConnections(!showConnections)}
                                            value="checkedB"
                                            color="primary"
                                            inputProps={{ 'aria-label': 'primary checkbox' }}
                                        />
                                    </AccordionDetails>
                                </Accordion>
                            </Paper>
                        )}
                    </div>
                )}
            </div>
            <div className="map-container" ref={mapContainer} />
            {kpis && (
                <div style={{ position: 'absolute', right: 10, top: 10 }}>
                    <Fab
                        color="default"
                        aria-describedby={id}
                        variant="round"
                        onClick={handleClickPopover}
                        size="small"
                    >
                        <InfoIcon />
                    </Fab>
                    <Popover
                        id={id}
                        open={openPopover}
                        anchorEl={anchorEl}
                        onClose={handleClosePopover}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                    >
                        {networkType ? (
                            <TableContainer component={Paper}>
                                <Table className={classes.table} aria-label="customized table">
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell>{report.networkType}</StyledTableCell>
                                            {renderTableHead(networkType)}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {infoIndicators[networkType].map(row => (
                                            <StyledTableRow key={row.name}>
                                                {renderTableRow(row)}
                                            </StyledTableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <p style={{ padding: '1em' }}>
                                Select network type to show table legend
                            </p>
                        )}
                    </Popover>
                </div>
            )}
            {kpis && (
                <div style={{ position: 'absolute', bottom: 30, right: 20 }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <Fab
                            color="default"
                            variant="round"
                            size="small"
                            style={{ margin: '1em 0' }}
                            onClick={() => setVisibleStatisticsModal(true)}
                        >
                            <EqualizerIcon />
                        </Fab>
                    </div>
                </div>
            )}
            {visibleStatisticsModal && (
                <Paper
                    elevation={3}
                    style={{
                        position: 'absolute',
                        left: '350px',
                        top: '10%',
                        width: 'calc(100% - 400px)',
                        padding: '1em',
                        height: 'auto',
                        maxHeight: '80vh',
                        overflowY: 'auto',
                        background: 'rgba(1,1,1,0.8)',
                    }}
                >
                    <div
                        className="header-graphucs"
                        style={{ width: '100%', height: '4em', borderBottom: '1px solid #ccc' }}
                    >
                        <div>
                            <h1 style={{ margin: 0, textAlign: 'center' }}>Graphics</h1>
                        </div>
                    </div>
                    <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 3 }}>
                        <IconButton
                            aria-label="close"
                            color="default"
                            variant="contained"
                            type="button"
                            onClick={() => setVisibleStatisticsModal(false)}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>
                    <div style={{ margin: '1em 0' }}>
                        <Statistics
                            kpis={kpis}
                            networkIndicator={networkIndicator}
                            networkType={networkType}
                        />
                    </div>
                </Paper>
            )}
        </div>
    )
}

export default ReportDetail
